import { Alert, AlertTitle, Box, Typography } from '@mui/material';

/**
 * Compute the single most relevant "next action" hint for a warranty ticket.
 *
 * Returns null when the ticket is in a healthy state with nothing to nudge
 * the technician about. Otherwise returns:
 *   { severity, title, message, secondary? }
 *
 * Severity meanings:
 *   - 'error'   — something is missing that blocks the warranty / parts flow
 *                 (e.g. defective model/serial for a parts ticket).
 *   - 'warning' — likely problem the tech should address soon
 *                 (overdue ETA, parts ordered by phone, follow-up in the past).
 *   - 'info'    — clear next step ("parts arrived, create Service Proposal").
 */
export function computeNextAction(ticket) {
  if (!ticket) return null;

  const status = ticket.status;
  const workStatus = ticket.workStatus;
  const parts = Array.isArray(ticket.parts) ? ticket.parts : [];

  // Closed tickets — quiet.
  if (status === 'Resolved') return null;

  // Statuses that imply parts/equipment work and therefore require
  // defective equipment identification for warranty claims.
  const PARTS_RELATED = new Set([
    'Parts needed',
    'Parts ordered',
    'Parts received',
  ]);

  // ----- HARD blockers (red) -----

  if (PARTS_RELATED.has(workStatus)) {
    const missingModel = !ticket.defectiveModel?.trim();
    const missingSerial = !ticket.defectiveSerial?.trim();
    if (missingModel || missingSerial) {
      const fields = [];
      if (missingModel) fields.push('model');
      if (missingSerial) fields.push('serial #');
      return {
        severity: 'error',
        title: 'Defective equipment ' + fields.join(' / ') + ' is missing',
        message:
          'Suppliers require the defective unit\'s model and serial number to honor a warranty claim. ' +
          'Fill them in the "System & Issue" section. ' +
          'If you don\'t remember, open CompanyCam for this property and check the equipment label photo.',
      };
    }
  }

  // ----- WARNINGS (orange) -----

  if (workStatus === 'Parts needed' && parts.length === 0) {
    return {
      severity: 'warning',
      title: 'No parts added yet',
      message:
        'You marked the work status as "Parts needed" but no parts are listed. ' +
        'Add at least one part with a supplier so the order can be sent and the warehouse is notified.',
    };
  }

  if (workStatus === 'Parts ordered' && parts.length === 0) {
    return {
      severity: 'warning',
      title: 'Parts ordered, but the list is empty',
      message:
        'The work status is "Parts ordered" but no parts are listed in the ticket. ' +
        'Add the ordered parts so the warehouse and dispatcher can see what is on the way.',
    };
  }

  if (workStatus === 'Parts ordered' && parts.length > 0) {
    const orderedByPhone = parts.filter(
      (p) => (p.orderedFrom || '').trim() === 'Ordered by Phone'
    );
    if (orderedByPhone.length > 0) {
      return {
        severity: 'warning',
        title: 'Some parts were ordered by phone',
        message:
          'For large units that ship to our warehouse the supplier MUST receive an email order with the ticket number — ' +
          'otherwise delivery is not tracked and the warehouse will not register the incoming unit. ' +
          'Edit each phone-ordered part, select the real supplier, and re-save to send the email.',
      };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const overdue = parts.filter((p) => {
      if (!p.expectedArrival) return false;
      const eta = new Date(p.expectedArrival);
      eta.setHours(0, 0, 0, 0);
      return eta < today;
    });
    if (overdue.length > 0) {
      return {
        severity: 'warning',
        title: 'Parts ETA is in the past',
        message:
          'The expected arrival date for one or more parts has passed. ' +
          'Call the supplier, confirm the new delivery date, and update ETA in the ticket.',
      };
    }
  }

  if (
    workStatus === 'Follow-up scheduled' &&
    (!ticket.followUpDate || !ticket.followUpTime)
  ) {
    return {
      severity: 'warning',
      title: 'Follow-up date or time is missing',
      message:
        'You marked the work status as "Follow-up scheduled" but the date and time are not set. ' +
        'Fill them in the "Follow-up" section so the visit appears in the schedule.',
    };
  }

  if (workStatus === 'Follow-up scheduled' && ticket.followUpDate) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const fu = new Date(ticket.followUpDate);
    fu.setHours(0, 0, 0, 0);
    if (fu < today) {
      return {
        severity: 'warning',
        title: 'Follow-up date is in the past',
        message:
          'The scheduled follow-up date has already passed. ' +
          'Either complete the visit and update the work status, or pick a new date.',
      };
    }
  }

  // ----- "What to do next" (blue) -----

  if (workStatus === 'Parts received' && status !== 'Resolved') {
    return {
      severity: 'info',
      title: 'Parts arrived — create a Service Proposal',
      message:
        'When parts are received, create a Service Proposal (PO-W1) so the install crew has a contract for the warranty repair. ' +
        'Use the "Create Service Proposal" button in this ticket.',
    };
  }

  if (workStatus === 'Completed on-site' && status !== 'Resolved') {
    return {
      severity: 'info',
      title: 'Work completed on-site — close the ticket',
      message:
        'You marked the work as completed on-site. ' +
        'Use the "Mark as Resolved" button to close the ticket and record completion notes.',
    };
  }

  return null;
}

/**
 * Visible banner at the top of the ticket page / dialog.
 * Renders nothing when no action is needed.
 */
export default function WarrantyTicketNextActionBanner({ ticket, sx }) {
  const action = computeNextAction(ticket);
  if (!action) return null;

  return (
    <Box sx={sx}>
      <Alert
        severity={action.severity}
        variant="standard"
        sx={{
          alignItems: 'flex-start',
          '& .MuiAlert-message': { width: '100%' },
        }}
      >
        <AlertTitle sx={{ fontWeight: 700 }}>{action.title}</AlertTitle>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {action.message}
        </Typography>
        {action.secondary && (
          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 1, color: 'text.secondary' }}
          >
            {action.secondary}
          </Typography>
        )}
      </Alert>
    </Box>
  );
}
